import { useState } from 'react'
import { Combobox } from '@headlessui/react'
import {
    CheckIcon,
    ChevronUpDownIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline'
import { Control, UseFormSetValue, useWatch } from 'react-hook-form'
import { useCategorySearch } from 'hooks/category-manage'
import { useDebounce } from 'hooks/useDebounce'
import { ArticleConnectedFormItem } from 'hooks/article'

const ArticleCategoryField = ({
    control,
    setValue,
    disabled,
    error,
}: {
    control: Control<any>
    setValue: UseFormSetValue<any>
    disabled?: boolean
    error?: string
}) => {
    const content: ArticleConnectedFormItem[] =
        useWatch({ control, name: 'connectedContent' }) || []
    const categories = content.filter((item) => item.contentType === 'category')
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounce(search.trim(), 400)
    const query = useCategorySearch(disabled ? '' : debouncedSearch)
    const searching = search.trim() !== debouncedSearch || query.isFetching
    const results = query.data?.results ?? []

    const selectCategories = (next: ArticleConnectedFormItem[]) => {
        if (disabled) return
        // Category uses the existing API relationship. Preserve older non-category links.
        const otherContent = content.filter(
            (item) => item.contentType !== 'category'
        )
        setValue('connectedContent', [...otherContent, ...next], {
            shouldDirty: true,
            shouldValidate: true,
        })
    }

    return (
        <div className="mt-4">
            <Combobox
                value={categories}
                multiple
                by="contentId"
                onChange={selectCategories}
                disabled={disabled}
            >
                <Combobox.Label className="article-field-label">
                    Category
                </Combobox.Label>
                <div className="relative">
                    <div className="flex items-center gap-2">
                        <div className="relative min-w-0 flex-1">
                            <Combobox.Input
                                className="w-full py-2 pl-3 pr-10"
                                value={search}
                                onChange={(event) =>
                                    setSearch(event.target.value)
                                }
                                placeholder="Search categories…"
                                aria-invalid={Boolean(error)}
                                aria-describedby="article-category-help"
                            />
                            <Combobox.Button
                                className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400"
                                aria-label="Show categories"
                            >
                                <ChevronUpDownIcon
                                    className="h-4 w-4"
                                    aria-hidden="true"
                                />
                            </Combobox.Button>
                        </div>
                    </div>
                    <Combobox.Options className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-lg focus:outline-none dark:border-slate-600 dark:bg-slate-800">
                        {!search.trim() ? (
                            <li
                                className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400"
                                role="presentation"
                            >
                                Type to search categories.
                            </li>
                        ) : searching ? (
                            <li
                                className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400"
                                role="presentation"
                            >
                                Searching…
                            </li>
                        ) : query.isError ? (
                            <li
                                className="px-3 py-2 text-xs text-red-500"
                                role="presentation"
                            >
                                Could not load categories. Try searching again.
                            </li>
                        ) : results.length === 0 ? (
                            <li
                                className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400"
                                role="presentation"
                            >
                                No categories found.
                            </li>
                        ) : (
                            results.map(
                                (result: { id: string; name: string }) => (
                                    <Combobox.Option
                                        key={result.id}
                                        value={{
                                            contentType: 'category',
                                            contentId: result.id,
                                            name: result.name,
                                        }}
                                        className={({ active }) =>
                                            `flex cursor-pointer items-center justify-between gap-2 px-3 py-2 ${
                                                active
                                                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200'
                                                    : 'text-slate-700 dark:text-slate-200'
                                            }`
                                        }
                                    >
                                        {({ selected }) => (
                                            <>
                                                <span className="truncate">
                                                    {result.name}
                                                </span>
                                                {selected && (
                                                    <CheckIcon
                                                        className="h-4 w-4 shrink-0"
                                                        aria-hidden="true"
                                                    />
                                                )}
                                            </>
                                        )}
                                    </Combobox.Option>
                                )
                            )
                        )}
                    </Combobox.Options>
                </div>
            </Combobox>
            {categories.length > 0 && (
                <ul
                    className="mt-2 flex flex-wrap gap-2"
                    aria-label="Selected categories"
                >
                    {categories.map((category) => (
                        <li
                            key={category.contentId}
                            className="inline-flex max-w-full items-center gap-1 rounded-md border border-blue-100 bg-blue-50 py-1 pl-2.5 pr-1 text-xs text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-200"
                        >
                            <span className="truncate">
                                {category.name || category.contentId}
                            </span>
                            <button
                                type="button"
                                disabled={disabled}
                                onClick={() =>
                                    selectCategories(
                                        categories.filter(
                                            (item) =>
                                                item.contentId !==
                                                category.contentId
                                        )
                                    )
                                }
                                aria-label={`Remove category ${
                                    category.name || category.contentId
                                }`}
                                className="rounded p-1 hover:bg-blue-100 disabled:opacity-40 dark:hover:bg-blue-800"
                            >
                                <XMarkIcon
                                    className="h-3.5 w-3.5"
                                    aria-hidden="true"
                                />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <p
                id="article-category-help"
                className={`mt-1.5 text-xs ${
                    error ? 'text-red-500' : 'text-slate-400'
                }`}
            >
                {error ||
                    'Search and select one or more categories for this article.'}
            </p>
        </div>
    )
}

export default ArticleCategoryField
